import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { CircularProgress, IconButton, TextField,  MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from '@mui/material/InputAdornment';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db, SECRETS_ENV } from "../../../Config/firebase.ts";
import { ActionType, SubscriptionType } from '../../../enums.ts';
import { getFutureCycles, logAction } from '../utilityFunctions.ts';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

const secretCloudTaskToken = import.meta.env.VITE_SECRET_CLOUD_TASK_TOKEN || process.env.VITE_SECRET_CLOUD_TASK_TOKEN

interface Item {
    name: string;
    price: string;
    quantity: string;
}

// Professional currency formatting function
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount).replace('₹', '₹ ');
};


export default function SingleRaiseInvoiceModal() {
    const Dispatch = useAppDispatch();
    const ServiceProviderID = useAppSelector(selectEffectiveUID) 
    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}
    const open = useAppSelector((state: RootState) => state.ModalSlice.SingleRaiseInvoiceModalOpen);
    const { subscriptionId, liveSubId } = useAppSelector((state: RootState) => state.ArbitrarySlice.ForSingleRaiseInvoiceModal);
    const [amount, setAmount] = React.useState<string>("");
    const [studentName, setStudentName] = React.useState("");
    const [subscriptionName, setSubscriptionName] = React.useState("");
    const [subscriptionType, setSubscriptionType] = React.useState<string>("");
    const [cyclePriceArray, setCyclePriceArray] = React.useState<any[]>([]);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
    const [error, setError] = React.useState({ amount: false });
    const [selectedCycle, setSelectedCycle] = React.useState<string>("");
    const [futureCycles, setFutureCycles] = React.useState<string[]>([]);
    const [invoiceCycleList, setInvoiceCycleList] = React.useState<string[]>([]);
    const [discountNegotiated, setDiscountNegotiated] = React.useState<number>(0);
    const [selectedCyclePriceArray, setSelectedCyclePriceArray] = React.useState<any>([]);
    const [currentPlanIndex, setCurrentPlanIndex] = React.useState<number>(0);
    
    // Items state
    const [items, setItems] = React.useState<Item[]>([]);
    const [itemErrors, setItemErrors] = React.useState<{name: boolean, price: boolean, quantity: boolean}[]>([]);



    // Close Modal Function
    function handleClose() {
        resetStates();
        Dispatch(closeModal("SingleRaiseInvoiceModalOpen"));
    }

    function resetStates() {
        setAmount("");
        setStudentName("");
        setSubscriptionName("");
        setError({ amount: false });
        setSelectedCycle("");
        setCyclePriceArray([]);
        setItems([]);
        setItemErrors([]);
    }

    // Fetch subscription and live subscription details
    React.useEffect(() => {
        const fetchData = async () => {
            if (subscriptionId && liveSubId) {
                try {
                    const subscriptionDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/Subscriptions/${subscriptionId}`);
                    const liveSubDocRef = doc(db, `ServiceProviders/${ServiceProviderID}/LiveSubscriptions/${liveSubId}`);

                    const [subscriptionDoc, liveSubDoc] = await Promise.all([
                        getDoc(subscriptionDocRef),
                        getDoc(liveSubDocRef)
                    ]);

                    if (subscriptionDoc.exists() && liveSubDoc.exists()) {
                        const liveSubData = liveSubDoc.data();

                        setSubscriptionName(liveSubData.subscriptionName || "");
                        setStudentName(liveSubData.studentName || "");
                        setSubscriptionType(liveSubData.subscriptionType);
                        setAmount(liveSubData.Price ? liveSubData.Price.toString() : "");
                        setInvoiceCycleList(liveSubData.invoiceCycleList || []);
                        setCyclePriceArray(liveSubData.assignedPlans || []);
                        setDiscountNegotiated(liveSubData.discountNegotiated || 0);
                        setCurrentPlanIndex(liveSubData.assignedPlanIndex || 0);

                        if (liveSubData.subscriptionType === SubscriptionType.Count || liveSubData.subscriptionType === SubscriptionType.MultiMonth) {
                            setSelectedCyclePriceArray(liveSubData.assignedPlans?.[liveSubData.assignedPlanIndex || 0]);
                        }

                        const cycles = getFutureCycles();
                        // Get first two cycles
                        const firstTwoCycles = cycles.slice(0, 2);
                        setFutureCycles(firstTwoCycles);
                        // Set default selected cycle to the first one
                        if (firstTwoCycles.length > 0) {
                            setSelectedCycle(firstTwoCycles[0]);

                        }
                    }
                } catch (error) {
                    console.error("Error fetching data:", error);
                }
            }
        };

        fetchData();
    }, [ServiceProviderID, subscriptionId, liveSubId, open]);

    // Items management functions
    const calculateItemTotal = (price: string, quantity: string) => {
        const priceNum = parseFloat(price) || 0;
        const quantityNum = parseFloat(quantity) || 0;
        return priceNum * quantityNum;
    };

    const calculateItemsGrandTotal = () => {
        return items.reduce((total, item) => {
            return total + calculateItemTotal(item.price, item.quantity);
        }, 0);
    };

    const calculateOverallTotal = () => {
        let invoiceAmount = 0;
        
        if (subscriptionType === SubscriptionType.Monthly) {
            invoiceAmount = parseFloat(amount) || 0;
        } else if (subscriptionType === SubscriptionType.MultiMonth || subscriptionType === SubscriptionType.Count) {
            invoiceAmount = selectedCyclePriceArray?.price || 0;
        }
        
        return invoiceAmount + calculateItemsGrandTotal();
    };

    const isItemComplete = (item: Item) => {
        return item.name.trim() !== '' && item.price.trim() !== '' && item.quantity.trim() !== '';
    };

    const canAddItem = () => {
        if (items.length === 0) return true;
        const lastItem = items[items.length - 1];
        return isItemComplete(lastItem);
    };

    const updateItem = (index: number, field: keyof Item, value: string) => {
        setItems(prev => prev.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
        ));
    };

    const addItem = () => {
        if (canAddItem()) {
            setItems(prev => [...prev, { name: '', price: '', quantity: '1' }]);
            setItemErrors(prev => [...prev, { name: false, price: false, quantity: false }]);
        }
    };

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(prev => prev.filter((_, i) => i !== index));
            setItemErrors(prev => prev.filter((_, i) => i !== index));
        } else if (items.length === 1) {
            setItems([]);
            setItemErrors([]);
        }
    };

    const handlePriceChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value)) {
            updateItem(index, 'price', value);
        }
    };

    const handleQuantityChange = (index: number, value: string) => {
        if (/^\d*\.?\d*$/.test(value) && value !== '0' && value !== '0.') {
            updateItem(index, 'quantity', value);
        }
    };

    // Onchange functions

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!value) {
            setAmount("");
            return;
        }
        // Only allow numbers and one decimal point
        if (/^\d*\.?\d*$/.test(value)) {
            setAmount(value);
        }
    };

    // Error Check Function

    const checkErrors = () => {
        let localerror = false;
        if (subscriptionType === SubscriptionType.Monthly) {
            if (Number(amount) <= 6) {
                localerror = true;
                setError({ ...error, amount: true })
            }
        }

        // Check items errors
        if (items.length > 0) {
            const newItemErrors: {name: boolean, price: boolean, quantity: boolean}[] = [];
            
            items.forEach((item, index) => {
                const errors = {
                    name: item.name.trim() === '',
                    price: parseFloat(item.price) < 6,
                    quantity: parseFloat(item.quantity) < 1
                };
                
                if (errors.name || errors.price || errors.quantity) {
                    localerror = true;
                }
                
                newItemErrors[index] = errors;
            });
            
            setItemErrors(newItemErrors);
        }

        return localerror;
    }


    const { axiosPostRequest } = useBackend("");

    // Handle cycle change
    // const handleCycleChange = (event: SelectChangeEvent) => {
    //     setSelectedCycle(event.target.value);
    // };

    async function handleRaiseInvoiceAPICall() {
        Dispatch(toggleLoading(true));

        try {
            let response;
            if (subscriptionType === SubscriptionType.Count || subscriptionType === SubscriptionType.MultiMonth) {
                const multiMonthDoc = {
                    providerId: ServiceProviderID,
                    livesubscriptionId: liveSubId,
                    orderType: "online",
                    price: Number(calculateOverallTotal()),
                    recurring: false,
                    cycleName: selectedCycle,
                    subscriptionId: subscriptionId,
                    secretCloudTaskToken: secretCloudTaskToken,
                    billingCycle: subscriptionType === SubscriptionType.Count ? "Count" : cyclePriceArray[currentPlanIndex]?.billingCycle,
                    cyclePriceArray: cyclePriceArray,
                    currentPlanIndex: currentPlanIndex,
                    billingStartTime: Timestamp.fromDate(new Date()),
                    billingEndTime: null,
                    bRaiseInvoice: true,
                    items: items.map(item => ({
                        name: item.name,
                        price: Number(item.price),
                        quantity: Number(item.quantity)
                    })),

                };


                response = await axiosPostRequest(
                    `${SECRETS_ENV.API_END_POINT}/api/quickOrderMultiMonth`,
                    multiMonthDoc
                );

                if (response.message !== "Request successful") {
                    Dispatch(toggleLoading(false));
                    return "error";
                }

            } else if (subscriptionType === SubscriptionType.Monthly) {
                const body = {
                    providerId: ServiceProviderID,
                    livesubscriptionId: liveSubId,
                    orderType: "online",
                    chargeable: Number(calculateOverallTotal()),
                    recurring: false,
                    cycleName: selectedCycle,
                    secretCloudTaskToken: secretCloudTaskToken,
                    raiseInvoice: true,
                    items: items.map(item => ({
                        name: item.name,
                        price: Number(item.price),
                        quantity: Number(item.quantity)
                    })),
                }

                const response = await axiosPostRequest(`${SECRETS_ENV.API_END_POINT}/api/quickOrder`, body)
                if (response.message !== "Request successful") {
                    return "error"
                }
                Dispatch(toggleLoading(false));
                return "success";
            }
        } catch (error) {
            Dispatch(toggleLoading(false));
            return "error";
        } finally {
            Dispatch(toggleLoading(false));
        }
    }

    async function onSubmit(e: React.FormEvent) {
        const localerror = checkErrors();
        if (localerror) {
            return;
        }
        e.preventDefault();
        // Check if invoice already exists for this cycle for monthly subscription
        if (subscriptionType === SubscriptionType.Monthly && invoiceCycleList.includes(selectedCycle)) {
            toast.error(`Invoice already exists for this ${selectedCycle}`)
            return;
        }
      
        const response = await handleRaiseInvoiceAPICall()
        if (response === "error") {
            toast.error("Error in raising invoice")
        } else {
            logAction({
                action: ActionType.raiseInvoiceIndividual,
                timestamp: Timestamp.now(),
                platform: "web",
                performedBy: {
                    spId: spId || "",
                    name: spName || ""
                },
                livesubscriptionId: liveSubId,
                context: {
                    livesubscriptionId: liveSubId,
                    priceRaised: amount ? Number(amount) : selectedCyclePriceArray.price,
                },
            }, ServiceProviderID)
            toast.success("Invoice raised successfully")
            handleClose();
        }
    }

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '10px' }
            }}
        >
            <DialogContent sx={{ padding: "40px" }}>
                <div className="header pb-6">
                    <p className="text-xl font-semibold text-gray-900">Raise Invoice</p>
                    <div className="absolute right-4 top-4">
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Customer Information */}
                    <div className='bg-gray-50 rounded-lg p-4 border border-gray-100'>
                        <p className="font-medium text-gray-900 mb-1">Student Name: {studentName}</p>
                        <p className="text-sm text-gray-600">Batch Name: {subscriptionName}</p>
                    </div>

                    {subscriptionType === SubscriptionType.Monthly && (
                        <>
                            <TextField
                                size={"small"}
                                id="cycle-select"
                                select
                                label="Select Month"
                                value={selectedCycle}
                                onChange={(e) => setSelectedCycle(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e5e7eb',
                                            borderRadius: '8px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#d1d5db',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '14px',
                                        },
                                    },
                                    fontWeight: 400,
                                    width: '100%',
                                    marginBottom: 2
                                }}
                            >
                                {futureCycles.map((cycle) => (
                                    <MenuItem key={cycle} sx={{ fontSize: '14px' }} value={cycle}>
                                        {cycle}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                value={amount}
                                size='small'
                                onChange={handleAmountChange}
                                label="Amount"
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                }}
                                error={error.amount}
                                helperText={error.amount ? "Amount must be greater than 6" : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e5e7eb',
                                            borderRadius: '8px'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#d1d5db',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3b82f6',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '14px',
                                            fontVariantNumeric: 'tabular-nums',
                                        },
                                    },
                                    fontWeight: 400,
                                    marginBottom: 2
                                }}
                            />
                        </>
                    )}

                    {subscriptionType === SubscriptionType.MultiMonth && cyclePriceArray.length > 0 && (
                        <div className="space-y-2">
                            <p className='font-semibold'>Invoice Will be Raised for </p>
                            <div className="space-y-2">

                                <p>Price : {formatCurrency(selectedCyclePriceArray.price)}</p>
                                <p>Billing Cycle : {selectedCyclePriceArray.billingCycle}</p>
                                <p>Duration : {selectedCyclePriceArray.validity} days</p>

                            </div>
                            {/* {discountNegotiated > 0 && (
                                <div className="mt-6 p-3  rounded-lg">
                                    <p className="text-blue-700 font-medium">
                                        Discount: ₹ {discountNegotiated}
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        This discount will be applied on top of the plan price upon selection by the subscriber.
                                    </p>
                                </div>
                            )} */}
                        </div>
                    )}

                    {subscriptionType === SubscriptionType.Count && cyclePriceArray.length > 0 && (
                        <div className="space-y-2">
                            <p className='font-semibold'>Invoice Will be Raised for </p>
                            <div className="space-y-2">

                                <p>Price : {formatCurrency(selectedCyclePriceArray.price)}</p>
                                <p>No. of count : {selectedCyclePriceArray.count}</p>

                            </div>
                            {/* {discountNegotiated > 0 && (
                                <div className="mt-6 p-3 rounded-lg">
                                    <p className="text-blue-700 font-medium">
                                        Discount: ₹ {discountNegotiated}
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        This discount will be applied on top of the plan price selected by the subscriber.
                                    </p>
                                </div>
                            )} */}
                        </div>
                    )}

                    {/* Items Section */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <h3 className="text-lg font-semibold">Additional Items</h3>
                                {items.length === 0 && (
                                    <button
                                        onClick={addItem}
                                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                    >
                                        + Add Items
                                    </button>
                                )}
                            </div>
                            {items.length > 0 && (
                                <div className="text-right">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Items Total</p>
                                    <p className="text-lg font-semibold text-gray-900 tabular-nums">{formatCurrency(calculateItemsGrandTotal())}</p>
                                </div>
                            )}
                        </div>

                        {items.length > 0 && (
                            <>
                                {items.map((item, index) => (
                                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="font-medium text-gray-700 text-sm">Item {index + 1}</h4>
                                            <div className="flex items-center gap-4">
                                                {calculateItemTotal(item.price, item.quantity) > 0 && (
                                                    <div className="text-right">
                                                        <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200">
                                                            <span className="text-sm font-semibold tabular-nums">
                                                                {formatCurrency(calculateItemTotal(item.price, item.quantity))}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => removeItem(index)}
                                                    className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <TextField
                                                value={item.name}
                                                size='small'
                                                onChange={(e) => updateItem(index, 'name', e.target.value)}
                                                label="Item Name"
                                                variant="outlined"
                                                fullWidth
                                                placeholder="Enter item name"
                                                error={itemErrors[index]?.name || false}
                                                helperText={itemErrors[index]?.name ? "Item name is required" : ""}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#e5e7eb',
                                                            borderRadius: '8px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#d1d5db',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#3b82f6',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                }}
                                            />

                                            <TextField
                                                value={item.price}
                                                size='small'
                                                onChange={(e) => handlePriceChange(index, e.target.value)}
                                                label="Price"
                                                variant="outlined"
                                                fullWidth
                                                placeholder="0.00"
                                                error={itemErrors[index]?.price || false}
                                                helperText={itemErrors[index]?.price ? "Minimum ₹6 required" : ""}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#e5e7eb',
                                                            borderRadius: '8px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#d1d5db',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#3b82f6',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                }}
                                            />

                                            <TextField
                                                value={item.quantity}
                                                size='small'
                                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                label="Quantity"
                                                variant="outlined"
                                                fullWidth
                                                placeholder="1.0"
                                                error={itemErrors[index]?.quantity || false}
                                                helperText={itemErrors[index]?.quantity ? "Minimum 1 required" : ""}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: '#e5e7eb',
                                                            borderRadius: '8px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: '#d1d5db',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#3b82f6',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                            fontVariantNumeric: 'tabular-nums',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}

                                <button
                                    onClick={addItem}
                                    disabled={!canAddItem()}
                                    className={`w-full py-3 border-2 border-dashed rounded-lg text-sm font-medium transition-all ${
                                        canAddItem() 
                                            ? 'border-blue-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50 cursor-pointer' 
                                            : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    + Add Item
                                </button>
                            </>
                        )}
                    </div>

                    {/* Professional Overall Total Section */}
                    {(calculateOverallTotal() > 0) && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-sm  text-gray-800 font-semibold">Total Chargeable Amount</p>
                                    <div className="text-xs text-gray-800 mt-1">
                                        {subscriptionType === SubscriptionType.Monthly && amount && (
                                            <span>Invoice: {formatCurrency(parseFloat(amount))}</span>
                                        )}
                                        {(subscriptionType === SubscriptionType.MultiMonth || subscriptionType === SubscriptionType.Count) && selectedCyclePriceArray?.price && (
                                            <span>Invoice: {formatCurrency(selectedCyclePriceArray.price)}</span>
                                        )}
                                        {items.length > 0 && calculateItemsGrandTotal() > 0 && (
                                            <span>{(subscriptionType === SubscriptionType.Monthly && amount) || ((subscriptionType === SubscriptionType.MultiMonth || subscriptionType === SubscriptionType.Count) && selectedCyclePriceArray?.price) ? ' + ' : ''}Items: {formatCurrency(calculateItemsGrandTotal())}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatCurrency(calculateOverallTotal())}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={"flex gap-2 pt-4"}>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSubmit} 
                            disabled={isLoading || calculateOverallTotal() === 0}
                            className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading || calculateOverallTotal() === 0 ? "cursor-not-allowed" : "cursor-pointer"}`}>
                            {isLoading && <CircularProgress sx={{ color: "white" }} size={20} />}
                            <p className={"ml-2"}>
                                {isLoading ? "Raising Invoice..." : `Raise Invoice ${calculateOverallTotal() > 0 ? formatCurrency(calculateOverallTotal()) : ''}`}
                            </p>
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}